import { useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { agentMap, toneSurface } from "@/lib/mockData";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

/**
 * DebateFeed — renders messages from the live SSE-driven boardroom debate.
 * Parent (BoardroomPage) feeds in `messages`, `goal`, and the streaming flag.
 */
export default function DebateFeed({ messages = [], goal = "", isStreaming = false }) {
  const containerRef = useRef(null);
  const isStreamingDebate = useStore((s) => s.isStreamingDebate);
  const empty = !isStreaming && !isStreamingDebate && messages.length === 0;

  const idleHints = useMemo(
    () => [
      {
        id: "intro-1",
        agentId: "echo",
        text: "Boardroom is warm. Drop a goal below to start a fresh debate — the team will weigh in agent by agent.",
      },
      {
        id: "intro-2",
        agentId: "ledger",
        text: "Each debate is saved automatically. Replays load instantly from the past-debates rail.",
      },
    ],
    [],
  );

  // auto-scroll feed as messages stream in
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  return (
    <section data-testid="debate-feed" className="card-soft rounded-[28px] p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="eyebrow">Debate Feed</div>
          <div className="mt-1 text-[15px] font-bold text-zinc-900">
            {empty
              ? "Awaiting your goal…"
              : `Boardroom debate · ${goal || "active goal"}`}
          </div>
        </div>
        {isStreaming && (
          <span
            data-testid="debate-feed-streaming"
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-violet-600 idea-dot-pulse" />
            Streaming
          </span>
        )}
      </div>

      <div
        ref={containerRef}
        className="idea-scroll max-h-[440px] min-h-[240px] space-y-3 overflow-y-auto pr-2"
      >
        {empty &&
          idleHints.map((m, i) => (
            <Bubble
              key={m.id}
              agent={agentMap[m.agentId]}
              text={m.text}
              isStreaming={false}
              index={i}
              testId={`debate-hint-${i}`}
            />
          ))}

        <AnimatePresence initial={false}>
          {messages.map((m, idx) => {
            const agent = agentMap[m.agentId] || {
              name: m.name || m.agentId,
              role: m.role || "",
              tone: "neutral",
            };
            return (
              <Bubble
                key={`${m.agentId}-${idx}`}
                agent={agent}
                text={m.content}
                isStreaming={!!m.streaming}
                isWaiting={m.streaming && !m.content}
                index={idx}
                testId={`debate-message-${idx}`}
              />
            );
          })}
        </AnimatePresence>
      </div>
    </section>
  );
}

function Bubble({ agent, text, isStreaming, isWaiting, index, testId }) {
  const isDark = agent?.tone === "dark";
  const tone = toneSurface[agent?.tone] ?? toneSurface.neutral;

  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 28, delay: 0.02 * index }}
      className="flex items-start gap-3"
    >
      <div
        className={cn(
          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-extrabold tracking-tight",
          isDark
            ? "bg-zinc-900 text-white"
            : "border border-black/[0.04] bg-white text-zinc-800",
        )}
      >
        {agent?.name?.[0] ?? "?"}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[13px] font-bold text-zinc-900">{agent?.name}</span>
          {agent?.role && (
            <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-400">
              {agent?.role}
            </span>
          )}
        </div>
        <div
          className={cn(
            "rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed",
            tone.bg,
            tone.quote,
            isDark ? "shadow-md" : "border border-black/[0.04]",
            isStreaming && "ring-1 ring-violet-200",
          )}
        >
          {isWaiting ? (
            <span data-testid="agent-typing" className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-zinc-400" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-zinc-400 [animation-delay:0.15s]" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-zinc-400 [animation-delay:0.3s]" />
            </span>
          ) : (
            <span className={cn(isStreaming && "idea-caret")}>{text}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
